import { CommonModule } from '@angular/common';
import { Component, forwardRef, inject, input, OnInit } from '@angular/core';
import {
   ControlContainer,
   ControlValueAccessor,
   FormGroup,
   FormGroupDirective,
   FormsModule,
   NG_VALUE_ACCESSOR,
   NgControl,
   ReactiveFormsModule,
} from '@angular/forms';
import { LabelInputComponent } from '@components/label-input/label-input.component';
import { ErrorMessageService } from '@core/services/error-message.service';
import { FunctionService } from '@core/utils/function.service';
import { TranslateModule } from '@ngx-translate/core';
import { CompanyService } from '@services/company.service';
import { TablerIconComponent } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/core/material.module';

@Component({
   selector: 'app-input-password',
   standalone: true,
   imports: [
      CommonModule,
      FormsModule,
      MaterialModule,
      ReactiveFormsModule,
      TranslateModule,
      LabelInputComponent,
      TablerIconComponent,
   ],
   providers: [CompanyService],
   viewProviders: [
      {
         provide: ControlContainer,
         useExisting: FormGroupDirective,
      },
      {
         provide: NG_VALUE_ACCESSOR,
         multi: true,
         useExisting: forwardRef(() => InputPasswordComponent),
      },
   ],
   templateUrl: './input-password.component.html',
})
export class InputPasswordComponent implements OnInit, ControlValueAccessor {
   // Variables
   form: FormGroup = new FormGroup({});
   hidePassword: boolean = true;

   // Services
   private parent = inject(FormGroupDirective);
   public errorMessageService = inject(ErrorMessageService);
   public functionService = inject(FunctionService);
   ngControl = inject(NgControl, { optional: true, self: true })!;

   // Inputs
   readonly nameControl = input<string>();
   readonly textLabel = input<string>();
   readonly required = input<boolean>(false);
   readonly maxlength = input<any>('9999');
   readonly type = input<string>('text');
   readonly value = input<any>();
   readonly textTooltip = input<string>();
   readonly hasTooltip = input<boolean>(false);
   readonly hasButtonCopy = input<boolean>(false);
   readonly placeholder = input<string>('');
   readonly disabled = input<boolean>(false);
   readonly textRequired = input<string>('');

   get f() {
      return this.form.controls;
   }

   constructor() {
      if (this.ngControl != null) this.ngControl.valueAccessor = this;
   }

   writeValue(value: any): void {
      if (this.f[this.nameControl()!]) {
         this.f[this.nameControl()!].setValue(value);
      }
   }

   onChange: (_: any) => void = (value: any) => {
      if (this.f[this.nameControl()!]) {
         this.f[this.nameControl()!].setValue(value);
      }
   };

   onTouched: () => void = () => {};

   registerOnChange(fn: any): void {
      this.onChange = fn;
   }

   registerOnTouched(fn: any): void {
      this.onTouched = fn;
   }

   ngOnInit() {
      this.form = this.parent.form;
   }

   hasErrorMessage(): boolean {
      const control = this.form.get(this.nameControl()!);
      return !!(control?.touched && control?.invalid && control?.errors);
   }

   getErrorMessage(): string | null {
      const control = this.form.get(this.nameControl()!);
      return this.errorMessageService.getErrorMessage(
         control!,
         this.nameControl()!,
         this.textRequired(),
      );
   }

   onCopy() {
      const value = this.value();
      this.functionService.copyTextToClipboard(value, 'InputValueCopied');
   }

   onChangeBlur() {
      this.f[this.nameControl()!].setValue(this.f[this.nameControl()!].value?.trim());
   }
}
